import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { registerSwrReset } from './registry';

/**
 * Factory for a per-id SWR cache backed by localStorage.
 * Used for resource-detail pages whose entries are cached independently by ID.
 *
 * - Cached entries render instantly on revisit.
 * - Every access triggers a background refetch; concurrent calls per id dedupe.
 * - LRU eviction when entry count exceeds `maxEntries`.
 * - Cache is keyed by userId; invalidated when a different user signs in.
 *
 * @param {Object} cfg
 * @param {string} cfg.name — devtools store name
 * @param {string} cfg.storageKey — localStorage key
 * @param {(id: string) => Promise<Object>} cfg.fetcher — returns the entry data
 * @param {number} [cfg.maxEntries=50] — LRU capacity
 */
export function createSwrKeyedStore({ name, storageKey, fetcher, maxEntries = 50 }) {
  const initialState = {
    entries: {}, // { [id]: { data, touchedAt } }
    cachedForUserId: null,
  };

  const inFlight = new Set(); // not persisted — reset per page load

  function evictIfNeeded(entries) {
    const keys = Object.keys(entries);
    if (keys.length <= maxEntries) return entries;
    const sorted = keys
      .map((k) => ({ k, t: entries[k]?.touchedAt || 0 }))
      .sort((a, b) => a.t - b.t);
    const toRemove = sorted.slice(0, keys.length - maxEntries).map((x) => x.k);
    const next = { ...entries };
    for (const k of toRemove) delete next[k];
    return next;
  }

  const useStore = create(
    devtools(
      persist(
        (set) => ({
          ...initialState,

          setEntry: (id, data) =>
            set((state) => {
              const nextEntries = {
                ...state.entries,
                [id]: { data, touchedAt: Date.now() },
              };
              return { entries: evictIfNeeded(nextEntries) };
            }),

          patchEntry: (id, updates) =>
            set((state) => {
              const existing = state.entries[id];
              if (!existing) return state;
              return {
                entries: {
                  ...state.entries,
                  [id]: {
                    data: { ...existing.data, ...updates },
                    touchedAt: Date.now(),
                  },
                },
              };
            }),

          removeEntry: (id) =>
            set((state) => {
              if (!state.entries[id]) return state;
              const next = { ...state.entries };
              delete next[id];
              return { entries: next };
            }),

          setCachedForUserId: (cachedForUserId) => set({ cachedForUserId }),

          reset: () => set(() => ({ ...initialState })),
        }),
        {
          name: storageKey,
          version: 1,
          storage: createJSONStorage(() => localStorage),
          partialize: (state) => ({
            entries: state.entries,
            cachedForUserId: state.cachedForUserId,
          }),
        }
      ),
      { name }
    )
  );

  async function fetchIfStale({ id, userId = null, force = false } = {}) {
    if (!id) return;
    const state = useStore.getState();

    if (userId && state.cachedForUserId && state.cachedForUserId !== userId) {
      state.reset();
    }

    if (inFlight.has(id) && !force) return;
    inFlight.add(id);

    try {
      const data = await fetcher(id);
      const next = useStore.getState();
      next.setEntry(id, data);
      if (userId) next.setCachedForUserId(userId);
    } catch (err) {
      // Preserve any existing cached entry; rethrow so the caller can surface errors
      // on cold-miss (no cache) scenarios.
      const existing = useStore.getState().entries[id];
      if (!existing) throw err;
    } finally {
      inFlight.delete(id);
    }
  }

  function useEntry(id, userId = null) {
    const entry = useStore((s) => (id ? s.entries[id] : null));
    const cachedForUserId = useStore((s) => s.cachedForUserId);

    const userMatches = !userId || !cachedForUserId || cachedForUserId === userId;
    const data = entry && userMatches ? entry.data : null;

    return {
      data,
      hasCache: !!data,
    };
  }

  registerSwrReset(() => useStore.getState().reset());

  return { useStore, useEntry, fetchIfStale };
}
